import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import CryptoJS from "crypto-js";

const AddPlaylistModal = ({ onClose, onSave }) => {
    const [formData, setFormData] = useState({
        profile_name: "",
        host: "",
        isXS: 0,
        username: "",
        password: "",
        isPIN: 0,
        pin: "",
        confirmPin: "",
    });
    const encryptPin = (pin) => {
        try {
            const encryptedPin = CryptoJS.AES.encrypt(
                JSON.stringify({ pin: pin }),
                'OctadevIPTV'
            ).toString();
            return encryptedPin;
        } catch (error) {
            console.error("Encryption failed:", error);
            return null;
        }
    };
    const [errors, setErrors] = useState({ host: "" });

    // Handle ESC key to close modal
    useEffect(() => {
        const handleEsc = (event) => {
            if (event.keyCode === 27) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEsc, false);
        return () => {
            document.removeEventListener('keydown', handleEsc, false);
        };
    }, [onClose]);

    // Handle clicking outside modal
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let errorMsg = "";

        if (name === "host") {
            // Validation
            if (!value.startsWith("http://") && !value.startsWith("https://")) {
                errorMsg = "Host must start with http:// or https://";
            } else if (value.endsWith("/")) {
                errorMsg = "Host cannot end with /";
            }
        }

        setFormData({
            ...formData,
            [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
        });
        setErrors({ ...errors, [name]: errorMsg });
    };
    const handleSubmit = () => {
        // Check for host validation errors
        if (errors.host) {
            alert("Please fix the host URL format before saving.");
            return;
        }

        // If isXS is 0, clear username and password
        if (formData.isXS === 0) {
            formData.username = "";
            formData.password = "";
        }
        // Validate PIN length if isPIN is 1
        if (formData.isPIN === 1 && formData.pin.length !== 4) {
            alert("PIN must be 4 numeric values!");
            return;
        }
        // Check if PINs match
        if (formData.isPIN === 1 && formData.pin !== formData.confirmPin) {
            alert("PINs do not match!");
            return;
        }

        // If isPIN is 0, clear pin and confirmPin
        if (formData.isPIN === 0) {
            formData.pin = "";
            formData.confirmPin = "";
        } else {
            formData.pin = encryptPin(formData.pin);
        }

        // Proceed with the save action
        onSave(formData);
        onClose();
    };


    return createPortal(
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div className="modal-container">
                <div className="modal-header">
                    <h2 className="modal-title">Add New Playlist</h2>
                    <button className="modal-close" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="modal-body">
                    <form className="playlist-form">
                        {/* Basic Information Section */}
                        <div className="form-section">
                            <h3 className="section-title">Basic Information</h3>
                            
                            <div className="form-group">
                                <label className="form-label">Profile Name</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    autoComplete="off"
                                    name="profile_name"
                                    value={formData.profile_name}
                                    onChange={handleChange}
                                    placeholder="Enter profile name"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Host URL</label>
                                <input
                                    type="text"
                                    className={`form-input ${errors.host ? 'error' : ''}`}
                                    autoComplete="off"
                                    name="host"
                                    placeholder={formData.isXS ? "http://server.com:80" : "http://server.com/index.m3u8"}
                                    value={formData.host}
                                    onChange={handleChange}
                                    required
                                />
                                {errors.host && (
                                    <span className="error-message">
                                        <i className="fas fa-exclamation-circle"></i>
                                        {errors.host}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Xtream Configuration Section */}
                        <div className="form-section">
                            <h3 className="section-title">Xtream Configuration</h3>
                            
                            <div className="form-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        className="checkbox-input"
                                        name="isXS"
                                        checked={formData.isXS === 1}
                                        onChange={handleChange}
                                    />
                                    <span className="checkbox-custom"></span>
                                    <span className="checkbox-text">Enable Xtream Playlist</span>
                                </label>
                            </div>

                            {formData.isXS === 1 && (
                                <div className="credentials-group">
                                    <div className="form-group">
                                        <label className="form-label">Username</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            name="username"
                                            value={formData.username}
                                            onChange={handleChange}
                                            autoComplete="off"
                                            placeholder="Enter username"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Password</label>
                                        <input
                                            type="password"
                                            className="form-input"
                                            name="password"
                                            value={formData.password}
                                            autoComplete="off"
                                            onChange={handleChange}
                                            placeholder="Enter password"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Security Section */}
                        <div className="form-section">
                            <h3 className="section-title">Security Settings</h3>
                            
                            <div className="form-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        className="checkbox-input"
                                        name="isPIN"
                                        checked={formData.isPIN === 1}
                                        onChange={handleChange}
                                    />
                                    <span className="checkbox-custom"></span>
                                    <span className="checkbox-text">Enable PIN Protection</span>
                                </label>
                            </div>

                            {formData.isPIN === 1 && (
                                <div className="pin-group">
                                    <div className="form-group">
                                        <label className="form-label">PIN (4 Digits)</label>
                                        <input
                                            type="text"
                                            className="form-input pin-input"
                                            name="pin"
                                            value={formData.pin}
                                            autoComplete="off"
                                            maxLength={4}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    pin: e.target.value.replace(/\D/g, ""),
                                                })
                                            }
                                            placeholder="0000"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Confirm PIN</label>
                                        <input
                                            type="text"
                                            className="form-input pin-input"
                                            name="confirmPin"
                                            value={formData.confirmPin}
                                            maxLength={4}
                                            autoComplete="off"
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    confirmPin: e.target.value.replace(/\D/g, ""),
                                                })
                                            }
                                            placeholder="0000"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </form>
                </div>

                <div className="modal-footer">
                    <button onClick={onClose} className="btn btn-secondary">
                        <i className="fas fa-times"></i>
                        Cancel
                    </button>
                    <button onClick={handleSubmit} className="btn btn-primary">
                        <i className="fas fa-save"></i>
                        Save Playlist
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AddPlaylistModal;
